// Carry-through helpers for the free-tier supplier acquisition flow.
//
// The *mechanism of record* for the plan/declarant association is the backend
// (bound to the authenticated Clerk userId via the invite token in
// unsafeMetadata + the authenticated /invite/claim call — see the design plan
// §4). These localStorage carriers are only a belt-and-suspenders UI hint used
// to steer onboarding and pick post-onboarding redirects; the client never
// asserts the plan itself.

import { publicPost } from 'helpers/requests';
import { FREE_TIER_SUPPLIER_PLAN } from 'modules/Common/permissions/planGating';

export { FREE_TIER_SUPPLIER_PLAN };

// localStorage keys.
export const FREE_TIER_INVITE_KEY = 'freeTierInvite';
// Existing flag (see Auth/MagicLink.jsx + OperatorInvites/OperatorInvitePage.jsx)
// reused to signal "a declarant connection is pending consent" to the /lite
// consent screen.
export const AUTO_ACCEPT_KEY = 'AutoAcceptDeclarantRequests';

export function setFreeTierInvite(invite) {
  try {
    window.localStorage.setItem(FREE_TIER_INVITE_KEY, JSON.stringify(invite || {}));
  } catch {
    /* storage unavailable — the backend claim still carries the association */
  }
}

export function getFreeTierInvite() {
  try {
    const raw = window.localStorage.getItem(FREE_TIER_INVITE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearFreeTierInvite() {
  try {
    window.localStorage.removeItem(FREE_TIER_INVITE_KEY);
  } catch {
    /* noop */
  }
}

export function hasFreeTierInvite() {
  return Boolean(getFreeTierInvite());
}

export function setAutoAcceptDeclarant() {
  try {
    window.localStorage.setItem(AUTO_ACCEPT_KEY, 'true');
  } catch {
    /* noop */
  }
}

export function isAutoAcceptPending() {
  try {
    return window.localStorage.getItem(AUTO_ACCEPT_KEY) === 'true';
  } catch {
    return false;
  }
}

export function clearAutoAcceptDeclarant() {
  try {
    window.localStorage.removeItem(AUTO_ACCEPT_KEY);
  } catch {
    /* noop */
  }
}

// POST /invite/verify — unauthenticated. Resolves an opaque invite token to the
// inviting declarant's identity so the landing page can show who invited the
// supplier before they sign up.
//   200 → { valid: true, declarantName, declarantLogo? }
//   4xx → { valid: false } (treated as an invalid/expired token)
export async function verifyInvite(token) {
  const res = await publicPost('/invite/verify', { token });
  return res.data;
}
